import { useEffect, useRef, useState } from 'react'

import { useConstantCallback } from '../../../hooks/useConstantCallback'
import { useLocationParams } from '../../../hooks/useLocationParams'
import { useVariableRef } from '../../../hooks/useVariableRef'
import type { PaginationValue } from '../../../utils/pagination'
import {
  getPageFromLocationParams,
  getPageSizeFromLocationParams,
  getPaginationLocationParams,
  isEqualPagination,
  PAGINATION_DEFAULT_PAGE_SIZE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
} from '../../../utils/pagination'
import type { ReactState } from '../../../utils/react'

export function useLocationPagination(): ReactState<PaginationValue> {
  const { locationParams, updateLocationParams } = useLocationParams()
  const updateLocationParamsRef = useRef(updateLocationParams)
  updateLocationParamsRef.current = updateLocationParams

  const pageRef = useVariableRef(getPageFromLocationParams(locationParams))
  const pageSizeRef = useVariableRef(
    getPageSizeFromLocationParams(locationParams)
  )

  const [value, setValue] = useState<PaginationValue>({
    count: 0,
    page: pageRef.current,
    pageSize: pageSizeRef.current,
  })

  useEffect(() => {
    setValue((currValue: PaginationValue) => {
      const { current: page } = pageRef
      const { current: pageSize } = pageSizeRef

      let nextValue = currValue

      if (nextValue.page !== page) {
        nextValue = { ...nextValue, page: page }
      }

      if (nextValue.pageSize !== pageSize) {
        nextValue = { ...nextValue, pageSize: pageSize }
      }

      return nextValue
    })
  }, [pageRef, pageSizeRef])

  const setPagination = useConstantCallback((nextValue: PaginationValue) => {
    const { current: page } = pageRef
    const { current: pageSize } = pageSizeRef
    const params = getPaginationLocationParams(nextValue)

    setValue((currValue: PaginationValue) =>
      isEqualPagination(currValue, nextValue) ? currValue : nextValue
    )

    const nextPage = params[PAGINATION_PAGE_PARAM] ?? null
    const nextPageSize = params[PAGINATION_PAGE_SIZE_PARAM] ?? null
    const currPage = page === 0 ? null : page
    const currPageSize =
      pageSize === PAGINATION_DEFAULT_PAGE_SIZE ? null : pageSize

    if (nextPage !== currPage || nextPageSize !== currPageSize) {
      updateLocationParamsRef.current({
        [PAGINATION_PAGE_PARAM]: nextPage,
        [PAGINATION_PAGE_SIZE_PARAM]: nextPageSize,
      })
    }
  })

  return [value, setPagination]
}
